import Resource from 'Resources/Resource';
import UserDefinedObject from './Model/UserDefinedObject';
import { UserDefinedObjectTypeId } from './Model/UserDefinedObjectType';
import Attributes from 'App/babel/attributes/UserDefinedObjectAttributes';
import { TYPE, STYLE } from 'Store/reducers/SystemNotificationReducer';
import T from 'Components/Helpers/translate';
import AppService from 'App/legacy/AppService';
import _ from 'lodash';
import Category from './Model/Category';
import CustomField, { CustomFieldWithValue } from './Model/CustomField';
import { parseCategories } from './parsers/client';
import { mapCustom } from './mappers/helpers';
import { parseDate } from './genericParser';

export type { UserDefinedObject };

type FindReturnType = Promise<{
	data: UserDefinedObject[];
	metadata: {
		offset: number;
		limit: number;
		total: number;
	};
}>;

const getUserDefinedObjectType = (typeId: number) => {
	const userDefinedObjectTypes = AppService.getMetadata().params.UserDefinedObject;
	return userDefinedObjectTypes.find(userDefinedObjectType => userDefinedObjectType.id === typeId) ?? null;
};

type ParsedCustomField = CustomField & { fieldId: number; value?: unknown };

export type EditableUserDefinedObject = Omit<UserDefinedObject, 'id' | 'custom' | 'categories'> & {
	id?: number;
	custom: ParsedCustomField[];
	categories: Category[];
	$mappedCustom?: { [id: number]: ParsedCustomField };
	$standardCategories?: Category[];
	$extraCategories?: { [categoryTypeId: number]: Category[] };
};

// Ported from ParseHelpers.parseCustom + ParseUserDefinedObject in ui/app/upsales/common/resources/parsers.
// parsers/helpers.ts parseCustom is not reused: it returns a map and rewrites value/default/dropdownDefault.
const parseUserDefinedObject = (typeId: UserDefinedObjectTypeId, object: EditableUserDefinedObject) => {
	const type = `userDefined${typeId}` as const;

	if (object.custom) {
		const values = object.custom;
		const mappedCustom: { [id: number]: ParsedCustomField } = {};
		object.custom = _.cloneDeep(AppService.getReadOnlyCustomFields(type)).map(field => {
			const cf = field as ParsedCustomField;
			const valueObj = values.find(f => f.fieldId === cf.id);
			cf.fieldId = cf.id;

			if (cf.datatype === 'Select' || cf.datatype === 'MultiSelect') {
				cf.dropdownDefault = Array.isArray(cf.dropdownDefault) ? cf.dropdownDefault[0] : cf.dropdownDefault;
			} else if (cf.datatype !== 'Calculation') {
				cf.default = Array.isArray(cf.default) ? cf.default[0] : cf.default;
			}

			if (valueObj) {
				cf.value = valueObj.value;
				switch (cf.datatype) {
					case 'Boolean':
						cf.value = !!cf.value;
						break;
					case 'Date':
						cf.value = parseDate(cf.value);
						break;
				}
			}
			cf.visible = !!cf.visible;
			cf.editable = !!cf.editable;
			mappedCustom[cf.id] = cf;
			return cf;
		});
		object.$mappedCustom = mappedCustom;
	}

	parseCategories(object, AppService.getCategories(type, true), AppService.getCategoryTypes(type));

	return object;
};

// Ported from MapGeneric + MapHelpers.mapCategories in ui/app/upsales/common/resources/mappers
const mapUserDefinedObject = (object: Partial<EditableUserDefinedObject>) => {
	const mapped: { [key: string]: unknown } = { ...object };

	if (object.custom) {
		mapped.custom = mapCustom(object.custom as CustomFieldWithValue[]);
	}
	if (object.$standardCategories) {
		mapped.categories = _.union(object.$standardCategories, ...Object.values(object.$extraCategories ?? {}));
	}

	return mapped;
};

type ErrorResult = { error: { translated?: string } };
type RequestOpts = { urlParams: { typeId: UserDefinedObjectTypeId } };

class UserDefinedObjectResource extends Resource {
	constructor() {
		super('userDefinedObjects/:typeId', Attributes);

		this.dateFields = [Attributes.regDate.field, Attributes.modDate.field];

		this.notifications = {
			// @ts-expect-error
			save: (data: UserDefinedObject, requestOpts: RequestOpts) => {
				const {
					urlParams: { typeId }
				} = requestOpts;
				const title = getUserDefinedObjectType(typeId)?.name ?? T('default.object');

				return {
					title,
					body: 'default.wasSaved',
					style: STYLE.SUCCESS,
					icon: 'save',
					type: TYPE.BODY
				};
			},
			saveError: ({ error }: ErrorResult, requestOpts: RequestOpts) => {
				const {
					urlParams: { typeId }
				} = requestOpts;
				const title = getUserDefinedObjectType(typeId)?.name ?? T('default.object');

				return {
					title,
					body: error.translated || 'default.unableToSave',
					style: STYLE.ERROR,
					icon: 'save',
					type: TYPE.BODY
				};
			},
			// @ts-expect-error
			delete: (data: UserDefinedObject, requestOpts: RequestOpts) => {
				const {
					urlParams: { typeId }
				} = requestOpts;
				const title = getUserDefinedObjectType(typeId)?.name ?? T('default.object');

				return {
					title,
					body: 'default.wasDeleted',
					style: STYLE.SUCCESS,
					icon: 'trash',
					type: TYPE.BODY
				};
			},
			// @ts-expect-error
			deleteError: ({ error }: ErrorResult, requestOpts: RequestOpts) => {
				const {
					urlParams: { typeId }
				} = requestOpts;
				const title = getUserDefinedObjectType(typeId)?.name ?? T('default.object');

				return {
					title,
					body: error.translated || 'default.unableToDelete',
					style: STYLE.ERROR,
					icon: 'trash',
					type: TYPE.BODY
				};
			}
		};
	}

	eventName(requestOpts: RequestOpts) {
		const {
			urlParams: { typeId }
		} = requestOpts;
		return `userDefinedObject${typeId}`;
	}

	find(typeId: UserDefinedObjectTypeId, filters: object = {}, opts: object = {}): FindReturnType {
		return super.find(filters, { ...opts, urlParams: { typeId } });
	}

	get(typeId: UserDefinedObjectTypeId, id: number, opts: object = {}): Promise<{ data: EditableUserDefinedObject }> {
		return super.get(id, { ...opts, urlParams: { typeId } }).then((res: { data: EditableUserDefinedObject }) => ({
			...res,
			data: parseUserDefinedObject(typeId, res.data)
		}));
	}

	// @ts-expect-error The base new() takes no typeId
	new(typeId: UserDefinedObjectTypeId): EditableUserDefinedObject {
		return parseUserDefinedObject(typeId, {
			notes: '',
			notes1: '',
			notes2: '',
			notes3: '',
			notes4: '',
			client: null,
			contact: null,
			project: null,
			user: AppService.getSelf(),
			custom: [],
			categories: [],
			userRemovable: true,
			userEditable: true,
			userDefinedObjectId: typeId
		} as unknown as EditableUserDefinedObject);
	}

	save(
		typeId: UserDefinedObjectTypeId,
		data: Partial<EditableUserDefinedObject>,
		opts: object = {}
	): Promise<{ data: EditableUserDefinedObject & Pick<UserDefinedObject, 'id'> }> {
		return super.save(mapUserDefinedObject(data), {
			...opts,
			urlParams: { typeId }
		});
	}

	async _afterSave(data: EditableUserDefinedObject, opts: RequestOpts) {
		return super._afterSave(parseUserDefinedObject(opts.urlParams.typeId, data), opts);
	}

	delete(typeId: UserDefinedObjectTypeId, id: number, opts: object = {}): Promise<void> {
		return super.delete(id, { ...opts, urlParams: { typeId } });
	}

	async linkTicket(typeId: UserDefinedObjectTypeId, objectId: number, ticketId: number) {
		return this._postRequest(
			`${objectId}/ticket`,
			{ ticketId },
			{ methodName: 'linkTicket', urlParams: { typeId } }
		).then(res => res.data);
	}

	async unlinkTicket(typeId: UserDefinedObjectTypeId, objectId: number, ticketId: number) {
		return this._deleteRequest(`${objectId}/ticket/${ticketId}`, {
			methodName: 'unlinkTicket',
			urlParams: { typeId }
		}).then(res => res.data);
	}
}

const resource = new UserDefinedObjectResource();

export default resource;
